/**
 * Leer la hoja de la carga inicial: un CSV o un Excel (.xlsx), sin librerías.
 *
 * **Sin librería a propósito.** Un lector de Excel en JavaScript son cientos
 * de kilobytes que bajarían también los locales que nunca importan nada, para
 * leer lo único que hace falta: la primera hoja, como texto. Un .xlsx es un
 * zip con XML dentro; el navegador ya trae con qué descomprimirlo
 * (`DecompressionStream`), y el XML de una hoja tiene una forma fija. Mismo
 * criterio que la cámara del escáner (`docs/15` §2.19).
 *
 * Lo que no se lee: fórmulas (se toma el último valor que guardó Excel, que
 * es lo que se ve), otras hojas que no sean la primera, ni el formato de las
 * celdas salvo para reconocer una fecha.
 */

/** La tabla de la hoja: filas de celdas en texto, tal como se ven. */
export type Tabla = string[][];

/**
 * Las celdas que en el Excel eran **números** y no texto, por fila.
 *
 * Un número guardado por Excel viaja sin separador de miles y con punto
 * decimal («1500», «1.125»): no es ambiguo. El mismo «1.500» escrito como
 * texto —o en un CSV— puede ser mil quinientos o uno y medio, y la carga
 * inicial tiene que preguntar (ver `ambiguo` en `cargaInicial.ts`). Aparte de
 * la tabla para no cambiar su forma: el resto de la hoja sigue siendo texto.
 */
const numericas = new WeakMap<readonly string[], Set<number>>();

/** Si la celda [columna] de [fila] era un número del Excel, no un texto. */
export function esNumeroDeExcel(fila: readonly string[], columna: number): boolean {
  return numericas.get(fila)?.has(columna) ?? false;
}

/** La hoja de un fichero que eligió alguien: por su nombre, CSV o Excel. */
export async function leerHoja(archivo: { name: string; arrayBuffer(): Promise<ArrayBuffer> }): Promise<Tabla> {
  const bytes = await archivo.arrayBuffer();

  if (/\.xlsx$/i.test(archivo.name)) return leerXlsx(bytes);
  if (/\.xls$/i.test(archivo.name)) {
    throw new Error(
      'Ese Excel es del formato viejo (.xls). Ábrelo y guárdalo como «Libro de Excel (.xlsx)» o como CSV.',
    );
  }

  return leerCsv(new TextDecoder('utf-8').decode(bytes));
}

/**
 * Un CSV, con el separador que tenga: el Excel de un equipo en español guarda
 * con punto y coma, el de uno en inglés con coma, y una copia de una pantalla
 * trae tabuladores. Se mira la primera línea.
 */
export function leerCsv(texto: string): Tabla {
  const limpio = texto.replace(/^﻿/, '');
  const primera = limpio.split(/\r?\n/, 1)[0] ?? '';
  const separador = [';', '\t', ','].reduce((mejor, candidato) =>
    primera.split(candidato).length > primera.split(mejor).length ? candidato : mejor,
  );

  const filas: Tabla = [];
  let fila: string[] = [];
  let celda = '';
  let entreComillas = false;

  for (let i = 0; i < limpio.length; i += 1) {
    const caracter = limpio[i]!;

    if (entreComillas) {
      if (caracter === '"' && limpio[i + 1] === '"') {
        celda += '"';
        i += 1;
      } else if (caracter === '"') {
        entreComillas = false;
      } else {
        celda += caracter;
      }
    } else if (caracter === '"') {
      entreComillas = true;
    } else if (caracter === separador) {
      fila.push(celda);
      celda = '';
    } else if (caracter === '\n' || caracter === '\r') {
      if (caracter === '\r' && limpio[i + 1] === '\n') i += 1;
      fila.push(celda);
      filas.push(fila);
      fila = [];
      celda = '';
    } else {
      celda += caracter;
    }
  }

  if (celda !== '' || fila.length > 0) {
    fila.push(celda);
    filas.push(fila);
  }

  return filas;
}

/** Un .xlsx: la primera hoja, con las cadenas compartidas ya puestas. */
export async function leerXlsx(bytes: ArrayBuffer): Promise<Tabla> {
  const entradas = entradasDelZip(new Uint8Array(bytes));
  const leer = async (nombre: string): Promise<string | null> => {
    const entrada = entradas.get(nombre);
    return entrada === undefined ? null : new TextDecoder('utf-8').decode(await entrada());
  };

  const libro = await leer('xl/workbook.xml');
  const relaciones = await leer('xl/_rels/workbook.xml.rels');
  const hoja = await leer(rutaDeLaPrimeraHoja(libro, relaciones));
  if (hoja === null) throw new Error('Ese Excel no tiene ninguna hoja que leer.');

  const compartidas = cadenasCompartidas((await leer('xl/sharedStrings.xml')) ?? '');
  const fechas = estilosDeFecha((await leer('xl/styles.xml')) ?? '');

  return celdasDeLaHoja(hoja, compartidas, fechas);
}

/** Cada fichero del zip, por su nombre, listo para descomprimir. */
function entradasDelZip(zip: Uint8Array): Map<string, () => Promise<Uint8Array>> {
  const vista = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);

  // El directorio central se busca desde el final: su firma es 0x06054b50.
  let fin = -1;
  for (let i = zip.length - 22; i >= Math.max(0, zip.length - 65_557); i -= 1) {
    if (vista.getUint32(i, true) === 0x06054b50) {
      fin = i;
      break;
    }
  }
  if (fin === -1) throw new Error('Ese fichero no es un Excel (.xlsx) que se pueda leer.');

  const total = vista.getUint16(fin + 10, true);
  let posicion = vista.getUint32(fin + 16, true);
  const entradas = new Map<string, () => Promise<Uint8Array>>();

  for (let n = 0; n < total; n += 1) {
    if (vista.getUint32(posicion, true) !== 0x02014b50) break;
    const metodo = vista.getUint16(posicion + 10, true);
    const comprimido = vista.getUint32(posicion + 20, true);
    const largoDelNombre = vista.getUint16(posicion + 28, true);
    const largoExtra = vista.getUint16(posicion + 30, true);
    const largoDelComentario = vista.getUint16(posicion + 32, true);
    const local = vista.getUint32(posicion + 42, true);
    const nombre = new TextDecoder('utf-8').decode(zip.subarray(posicion + 46, posicion + 46 + largoDelNombre));

    entradas.set(nombre, async () => {
      // La cabecera local repite el nombre y puede traer otro «extra».
      const inicio =
        local + 30 + vista.getUint16(local + 26, true) + vista.getUint16(local + 28, true);
      const datos = zip.subarray(inicio, inicio + comprimido);
      if (metodo === 0) return datos;
      if (metodo !== 8) throw new Error('Ese Excel viene comprimido de una forma que no se sabe leer.');
      return descomprimir(datos);
    });

    posicion += 46 + largoDelNombre + largoExtra + largoDelComentario;
  }

  return entradas;
}

async function descomprimir(datos: Uint8Array): Promise<Uint8Array> {
  const flujo = new Blob([datos]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(flujo).arrayBuffer());
}

/** La primera hoja del libro, por su relación; la de siempre si no se halla. */
function rutaDeLaPrimeraHoja(libro: string | null, relaciones: string | null): string {
  const primera = /<sheet\b[^>]*\br:id="([^"]+)"/.exec(libro ?? '')?.[1];
  if (primera !== undefined && relaciones !== null) {
    const relacion = new RegExp(`<Relationship\\b[^>]*\\bId="${primera}"[^>]*>`).exec(relaciones)?.[0];
    const destino = relacion === undefined ? undefined : /\bTarget="([^"]+)"/.exec(relacion)?.[1];
    if (destino !== undefined) {
      return destino.startsWith('/') ? destino.slice(1) : `xl/${destino.replace(/^\.\//, '')}`;
    }
  }
  return 'xl/worksheets/sheet1.xml';
}

/** Las cadenas compartidas, en orden: una celda de texto dice cuál es la suya. */
function cadenasCompartidas(xml: string): string[] {
  return [...xml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((si) =>
    [...si[1]!.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => textoXml(t[1]!)).join(''),
  );
}

/**
 * Qué estilos de celda son fechas. Excel guarda una fecha como un número de
 * días desde 1900 y solo su formato dice que es una fecha: sin mirarlo, el
 * vencimiento «31/03/2027» llegaba como «46477».
 */
function estilosDeFecha(xml: string): Set<number> {
  const propios = new Map<number, string>();
  for (const formato of xml.matchAll(/<numFmt\b[^>]*numFmtId="(\d+)"[^>]*formatCode="([^"]*)"/g)) {
    propios.set(Number(formato[1]), formato[2]!);
  }

  const esFecha = (id: number): boolean => {
    if ((id >= 14 && id <= 22) || (id >= 45 && id <= 47)) return true;
    const codigo = propios.get(id);
    return codigo !== undefined && /[dy]/i.test(codigo.replace(/"[^"]*"|\[[^\]]*\]/g, ''));
  };

  const celdas = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml)?.[1] ?? '';
  const fechas = new Set<number>();
  [...celdas.matchAll(/<xf\b[^>]*>/g)].forEach((xf, indice) => {
    const id = /numFmtId="(\d+)"/.exec(xf[0])?.[1];
    if (id !== undefined && esFecha(Number(id))) fechas.add(indice);
  });
  return fechas;
}

function celdasDeLaHoja(xml: string, compartidas: string[], fechas: Set<number>): Tabla {
  const tabla: Tabla = [];

  // Una fila vacía puede venir cerrada sobre sí misma (`<row r="5"/>`).
  for (const fila of xml.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const numero = Number(/\br="(\d+)"/.exec(fila[1]!)?.[1] ?? tabla.length + 1);
    const celdas: string[] = [];
    const deNumero = new Set<number>();

    for (const celda of (fila[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const atributos = celda[1]!;
      const contenido = celda[2] ?? '';
      const referencia = /\br="([A-Z]+)\d+"/.exec(atributos)?.[1];
      const columna = referencia === undefined ? celdas.length : indiceDeColumna(referencia);
      const tipo = /\bt="([^"]+)"/.exec(atributos)?.[1];
      const estilo = Number(/\bs="(\d+)"/.exec(atributos)?.[1] ?? -1);
      const valor = /<v>([\s\S]*?)<\/v>/.exec(contenido)?.[1];

      let texto = '';
      if (tipo === 's' && valor !== undefined) texto = compartidas[Number(valor)] ?? '';
      else if (tipo === 'inlineStr') {
        texto = [...contenido.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => textoXml(t[1]!)).join('');
      } else if (valor !== undefined) {
        texto = textoXml(valor);
        if (tipo === undefined && fechas.has(estilo) && /^\d+(\.\d+)?$/.test(texto)) {
          texto = diaDeExcel(Number(texto));
        } else if ((tipo === undefined || tipo === 'n') && /^-?\d+(\.\d+)?$/.test(texto)) {
          deNumero.add(columna);
        }
      }

      while (celdas.length < columna) celdas.push('');
      celdas[columna] = texto;
    }
    if (deNumero.size > 0) numericas.set(celdas, deNumero);

    // Las filas vacías que Excel se salta se respetan: el número de fila que
    // se enseña es el que se ve en Excel.
    while (tabla.length < numero - 1) tabla.push([]);
    tabla.push(celdas);
  }

  return tabla;
}

/** `"AB"` → 27. */
function indiceDeColumna(letras: string): number {
  return [...letras].reduce((total, letra) => total * 26 + (letra.charCodeAt(0) - 64), 0) - 1;
}

/** El número de días de Excel como `2027-03-31`. */
export function diaDeExcel(serie: number): string {
  // El día 0 de Excel es el 30 de diciembre de 1899 (por el 29 de febrero de
  // 1900 que nunca existió). Al mediodía UTC, para que ninguna zona lo mueva.
  const fecha = new Date(Date.UTC(1899, 11, 30, 12) + Math.floor(serie) * 86_400_000);
  return fecha.toISOString().slice(0, 10);
}

function textoXml(texto: string): string {
  return texto
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, codigo: string) => String.fromCharCode(Number(codigo)))
    .replace(/&amp;/g, '&');
}
